import { memo, useState, useRef, useEffect } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Text, textVariants } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
  variant?: "body" | "caption";
  tone?: "default" | "muted";
  gradientColor?: string;
}

const LAST_LINE_FADE: React.CSSProperties = {
  maskImage:
    "linear-gradient(black, black), linear-gradient(to right, black calc(100% - 7rem), transparent calc(100% - 4.5rem))",
  maskSize: "100% calc(100% - 1lh), 100% 1lh",
  maskPosition: "top, bottom",
  maskRepeat: "no-repeat",
};

const LINE_CLAMP_CLASSES: Record<number, string> = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
  6: "line-clamp-6",
} as const;

export const ExpandableText = memo(
  ({
    text,
    maxLines = 3,
    className,
    variant = "body",
    tone = "muted",
  }: ExpandableTextProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const checkOverflow = () => {
        const element = textRef.current;
        if (!element) return;

        const isOverflow = element.scrollHeight > element.clientHeight;
        setIsOverflowing(isOverflow);
      };

      checkOverflow();
      window.addEventListener("resize", checkOverflow);
      return () => window.removeEventListener("resize", checkOverflow);
    }, [text]);

    const handleToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    };

    const lineClampClass =
      !isExpanded && LINE_CLAMP_CLASSES[maxLines]
        ? LINE_CLAMP_CLASSES[maxLines]
        : undefined;

    return (
      <div className="relative">
        <div
          ref={textRef}
          className={cn(
            textVariants({ variant }),
            "whitespace-pre-wrap",
            lineClampClass,
            className,
          )}
          style={isOverflowing && !isExpanded ? LAST_LINE_FADE : undefined}
        >
          <Text variant={variant} tone={tone}>
            {text}
          </Text>
        </div>
        {isOverflowing && !isExpanded && (
          <Button
            variant="link"
            onClick={handleToggle}
            className="absolute right-0 bottom-0 h-auto p-0"
          >
            see more
          </Button>
        )}
        {isExpanded && (
          <Button
            variant="link"
            onClick={handleToggle}
            className="mt-1 h-auto p-0"
          >
            see less
          </Button>
        )}
      </div>
    );
  },
);

ExpandableText.displayName = "ExpandableText";
